import { DayType, PrayerType } from '../types';

/**
 * The shul year set-up (Phase 6, docs/day-types-spec.md): one question per
 * prayer and kind of day. Used by the guided set-up (A) and the year checklist (B).
 * Texts: i18n `setup.screens.<screen>` and `setup.help.<question id>` (the "!" text).
 *
 * kind:
 * - 'required': no fallback; empty = "Not set" (no minyan shown).
 * - 'fallback': empty = follows `sameAs` (the app falls back by itself).
 * - 'optional': empty = none (e.g. Yizkor, Hakafot, Selichos on fasts).
 * A 'required' question may still offer "Same as …": then the answer adds this
 * day to those entries, so they stay linked.
 */
export type SetupScreenId =
  | 'weekday' | 'monThu' | 'friday' | 'shabbos' | 'roshChodesh' | 'chanukah' | 'fasts'
  | 'purim' | 'beinHazmanim' | 'selichot' | 'yomTov' | 'roshHashana' | 'yomKippur' | 'sukkot' | 'pesach'
  | 'shavuot' | 'tishaBav';

export type QuestionKind = 'required' | 'fallback' | 'optional';

export interface SetupQuestion {
  id: string;
  screen: SetupScreenId;
  prayer: PrayerType;
  dayType: DayType;
  /** Evening prayers: the night that starts the day, or the night after it. */
  night?: 'start' | 'end';
  kind: QuestionKind;
  /** The question whose times "Same as …" means. */
  sameAs?: string;
  /** On a weekday-family day: own times replace all weekday times ('*'). */
  replacesWeekday?: boolean;
}

const q = (
  id: string,
  screen: SetupScreenId,
  prayer: PrayerType,
  dayType: DayType,
  kind: QuestionKind,
  extra: Partial<SetupQuestion> = {}
): SetupQuestion => ({ id, screen, prayer, dayType, kind, ...extra });

export const SETUP_SCREENS: SetupScreenId[] = [
  'weekday', 'monThu', 'friday', 'shabbos', 'roshChodesh', 'chanukah', 'fasts', 'purim',
  'beinHazmanim', 'selichot', 'yomTov', 'roshHashana', 'yomKippur', 'sukkot', 'pesach', 'shavuot', 'tishaBav',
];

export const SETUP_QUESTIONS: SetupQuestion[] = [
  q('wd.shacharit', 'weekday', 'shacharit', 'weekday', 'required'),
  q('wd.mincha', 'weekday', 'mincha', 'weekday', 'required'),
  q('wd.maariv', 'weekday', 'maariv', 'weekday', 'required', { night: 'end' }),

  q('mt.shacharit', 'monThu', 'shacharit', 'sheniHamishi', 'fallback', { sameAs: 'wd.shacharit', replacesWeekday: true }),

  q('fri.shacharit', 'friday', 'shacharit', 'erevShabbos', 'fallback', { sameAs: 'wd.shacharit' }),
  q('fri.mincha', 'friday', 'mincha', 'erevShabbos', 'required'),

  q('shab.kabbalat', 'shabbos', 'kabalatShabbat', 'shabbos', 'required'),
  q('shab.friMaariv', 'shabbos', 'maariv', 'shabbos', 'required', { night: 'start' }),
  q('shab.shacharit', 'shabbos', 'shacharit', 'shabbos', 'required'),
  q('shab.musaf', 'shabbos', 'musaf', 'shabbos', 'optional'),
  q('shab.mincha', 'shabbos', 'mincha', 'shabbos', 'required'),
  q('shab.motzash', 'shabbos', 'maariv', 'shabbos', 'required', { night: 'end' }),

  q('rc.shacharit', 'roshChodesh', 'shacharit', 'roshChodesh', 'fallback', { sameAs: 'wd.shacharit', replacesWeekday: true }),

  q('chan.shacharit', 'chanukah', 'shacharit', 'chanukah', 'fallback', { sameAs: 'wd.shacharit', replacesWeekday: true }),
  q('chan.mincha', 'chanukah', 'mincha', 'chanukah', 'fallback', { sameAs: 'wd.mincha', replacesWeekday: true }),
  q('chan.maariv', 'chanukah', 'maariv', 'chanukah', 'fallback', { sameAs: 'wd.maariv', night: 'end', replacesWeekday: true }),
  q('chan.friMincha', 'chanukah', 'mincha', 'chanukahFriday', 'fallback', { sameAs: 'fri.mincha' }),
  q('chan.motzash', 'chanukah', 'maariv', 'chanukahShabbos', 'fallback', { sameAs: 'shab.motzash', night: 'end' }),

  q('fast.selichot', 'fasts', 'selichot', 'fastMinor', 'optional'),
  q('fast.shacharit', 'fasts', 'shacharit', 'fastMinor', 'fallback', { sameAs: 'wd.shacharit' }),
  q('fast.mincha', 'fasts', 'mincha', 'fastMinor', 'required', { sameAs: 'wd.mincha' }),
  q('fast.maariv', 'fasts', 'maariv', 'fastMinor', 'required', { sameAs: 'wd.maariv', night: 'end' }),
  q('fast.friMincha', 'fasts', 'mincha', 'fastFriday', 'fallback', { sameAs: 'fast.mincha' }),
  q('fast.friNight', 'fasts', 'maariv', 'fastFriday', 'fallback', { sameAs: 'shab.friMaariv', night: 'start' }),

  q('purim.night', 'purim', 'maariv', 'purim', 'required', { night: 'start' }),
  q('purim.megNight', 'purim', 'megillah', 'purim', 'optional', { night: 'start' }),
  q('purim.megDay', 'purim', 'megillah', 'purim', 'optional'),
  q('purim.shacharit', 'purim', 'shacharit', 'purim', 'required', { sameAs: 'wd.shacharit' }),
  q('purim.mincha', 'purim', 'mincha', 'purim', 'required', { sameAs: 'wd.mincha' }),
  q('purim.maariv', 'purim', 'maariv', 'purim', 'required', { sameAs: 'wd.maariv', night: 'end' }),
  q('purim.friShacharit', 'purim', 'shacharit', 'purimFriday', 'fallback', { sameAs: 'purim.shacharit' }),
  q('purim.friMincha', 'purim', 'mincha', 'purimFriday', 'fallback', { sameAs: 'purim.mincha' }),
  q('purim.friNight', 'purim', 'maariv', 'purimFriday', 'fallback', { sameAs: 'shab.friMaariv', night: 'start' }),

  q('bh.shacharit', 'beinHazmanim', 'shacharit', 'beinHazmanim', 'fallback', { sameAs: 'wd.shacharit', replacesWeekday: true }),
  q('bh.mincha', 'beinHazmanim', 'mincha', 'beinHazmanim', 'fallback', { sameAs: 'wd.mincha', replacesWeekday: true }),
  q('bh.maariv', 'beinHazmanim', 'maariv', 'beinHazmanim', 'fallback', { sameAs: 'wd.maariv', night: 'end', replacesWeekday: true }),

  q('sel.firstNight', 'selichot', 'selichot', 'selichotFirstNight', 'optional'),
  q('sel.beforeRH', 'selichot', 'selichot', 'selichotBeforeRH', 'optional'),
  q('sel.erevRH', 'selichot', 'selichot', 'erevRoshHashana', 'fallback', { sameAs: 'sel.beforeRH' }),
  q('sel.ayt', 'selichot', 'selichot', 'aseretYemeiTeshuva', 'fallback', { sameAs: 'sel.beforeRH' }),
  q('sel.tzomGedalia', 'selichot', 'selichot', 'tzomGedalia', 'fallback', { sameAs: 'sel.ayt' }),
  q('sel.erevYK', 'selichot', 'selichot', 'erevYomKippur', 'fallback', { sameAs: 'sel.ayt' }),
  q('sel.behab', 'selichot', 'selichot', 'behab', 'optional'),

  q('eyt.shacharit', 'yomTov', 'shacharit', 'erevYomTov', 'fallback', { sameAs: 'wd.shacharit' }),
  q('eyt.mincha', 'yomTov', 'mincha', 'erevYomTov', 'required'),
  q('yt.night', 'yomTov', 'maariv', 'yomTov', 'required', { night: 'start' }),
  q('yt.shacharit', 'yomTov', 'shacharit', 'yomTov', 'required'),
  q('yt.musaf', 'yomTov', 'musaf', 'yomTov', 'optional'),
  q('yt.mincha', 'yomTov', 'mincha', 'yomTov', 'required'),
  q('yt.out', 'yomTov', 'maariv', 'yomTov', 'required', { night: 'end' }),

  q('rh.shacharit', 'roshHashana', 'shacharit', 'roshHashana', 'fallback', { sameAs: 'yt.shacharit' }),
  q('rh.night', 'roshHashana', 'maariv', 'roshHashana', 'fallback', { sameAs: 'yt.night', night: 'start' }),
  q('rh.tekiot', 'roshHashana', 'tekiot', 'roshHashana', 'optional'),
  q('rh.musaf', 'roshHashana', 'musaf', 'roshHashana', 'fallback', { sameAs: 'yt.musaf' }),
  q('rh.mincha', 'roshHashana', 'mincha', 'roshHashana', 'fallback', { sameAs: 'yt.mincha' }),
  q('rh.friMincha', 'roshHashana', 'mincha', 'roshHashanaFriday', 'required'),

  q('eyk.shacharit', 'yomKippur', 'shacharit', 'erevYomKippur', 'required', { sameAs: 'wd.shacharit' }),
  q('eyk.mincha', 'yomKippur', 'mincha', 'erevYomKippur', 'required'),
  q('yk.zakha', 'yomKippur', 'tefilahZakha', 'yomKippur', 'optional'),
  q('yk.kolNidrei', 'yomKippur', 'maariv', 'yomKippur', 'required', { night: 'start' }),
  q('yk.shacharit', 'yomKippur', 'shacharit', 'yomKippur', 'required'),
  q('yk.yizkor', 'yomKippur', 'yizkor', 'yomKippur', 'optional'),
  q('yk.musaf', 'yomKippur', 'musaf', 'yomKippur', 'optional'),
  q('yk.mincha', 'yomKippur', 'mincha', 'yomKippur', 'required'),
  q('yk.neila', 'yomKippur', 'neila', 'yomKippur', 'required'),
  q('yk.out', 'yomKippur', 'maariv', 'yomKippur', 'required', { night: 'end' }),

  q('suk.night', 'sukkot', 'maariv', 'sukkotI', 'fallback', { sameAs: 'yt.night', night: 'start' }),
  q('suk.shacharit', 'sukkot', 'shacharit', 'sukkotI', 'fallback', { sameAs: 'yt.shacharit' }),
  q('chm.shacharit', 'sukkot', 'shacharit', 'cholHaMoed', 'fallback', { sameAs: 'wd.shacharit' }),
  q('chm.mincha', 'sukkot', 'mincha', 'cholHaMoed', 'fallback', { sameAs: 'wd.mincha' }),
  q('chm.maariv', 'sukkot', 'maariv', 'cholHaMoed', 'fallback', { sameAs: 'wd.maariv', night: 'end' }),
  q('hr.shacharit', 'sukkot', 'shacharit', 'hoshanaRabba', 'fallback', { sameAs: 'chm.shacharit' }),
  q('st.night', 'sukkot', 'maariv', 'simchatTorah', 'fallback', { sameAs: 'yt.night', night: 'start' }),
  q('st.hakNight', 'sukkot', 'hakafot', 'simchatTorah', 'optional', { night: 'start' }),
  q('st.shacharit', 'sukkot', 'shacharit', 'simchatTorah', 'fallback', { sameAs: 'yt.shacharit' }),
  q('st.hakDay', 'sukkot', 'hakafot', 'simchatTorah', 'optional'),
  q('st.yizkor', 'sukkot', 'yizkor', 'simchatTorah', 'optional'),

  q('pes.night', 'pesach', 'maariv', 'pesachI', 'fallback', { sameAs: 'yt.night', night: 'start' }),
  q('pes.shacharit', 'pesach', 'shacharit', 'pesachI', 'fallback', { sameAs: 'yt.shacharit' }),
  q('p7.night', 'pesach', 'maariv', 'shviiShelPesach', 'fallback', { sameAs: 'yt.night', night: 'start' }),
  q('p7.shacharit', 'pesach', 'shacharit', 'shviiShelPesach', 'fallback', { sameAs: 'yt.shacharit' }),
  q('p7.yizkor', 'pesach', 'yizkor', 'shviiShelPesach', 'optional'),

  q('shav.night', 'shavuot', 'maariv', 'shavuot', 'fallback', { sameAs: 'yt.night', night: 'start' }),
  q('shav.shacharit', 'shavuot', 'shacharit', 'shavuot', 'fallback', { sameAs: 'yt.shacharit' }),
  q('shav.yizkor', 'shavuot', 'yizkor', 'shavuot', 'optional'),
  q('ytf.mincha', 'shavuot', 'mincha', 'yomTovFriday', 'required'),
  q('ytf.night', 'shavuot', 'maariv', 'yomTovFriday', 'fallback', { sameAs: 'shab.friMaariv', night: 'start' }),

  q('etb.mincha', 'tishaBav', 'mincha', 'erevTishaBav', 'required'),
  q('tb.night', 'tishaBav', 'maariv', 'tishaBav', 'required', { night: 'start' }),
  q('tb.shacharit', 'tishaBav', 'shacharit', 'tishaBav', 'required'),
  q('tb.mincha', 'tishaBav', 'mincha', 'tishaBav', 'required'),
];

export function questionById(id: string): SetupQuestion | undefined {
  return SETUP_QUESTIONS.find((x) => x.id === id);
}
